/**
 * AcceptInvite.jsx — AIRGROW Material Interface
 * 招待リンクからの組織参加ページ
 * URL: /invite/:token
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getInvitation, acceptInvitation } from '../services/userApi';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import InfoCell from '../components/ui/InfoCell';
import StatusBadge from '../components/ui/StatusBadge';
import AirgrowBrand from '../components/ui/AirgrowBrand';
import { C } from '../ColorTokens';

const ROLE_LABELS = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

// ---- 共通: フルページセンタリングラッパー ----
function PageShell({ children }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ background: C.background, padding: '24px' }}
    >
      {/* ブランドロゴ */}
      <div className="mb-8">
        <AirgrowBrand size="sm" layout="horizontal" />
      </div>

      {/* カード */}
      <div
        className="w-full glass-panel"
        style={{
          maxWidth: '440px',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0,104,183,0.08)',
        }}
      >
        {children}
      </div>

      {/* Copyright */}
      <p
        style={{
          marginTop: '24px',
          fontFamily: 'Space Grotesk',
          fontSize: '10px',
          letterSpacing: '0.3px',
          color: C.outline,
        }}
      >
        © 2026 NTT e-Drone Technology Co., Ltd.
      </p>
    </div>
  );
}

// ---- 共通: ステータスアイコン円 ----
function StatusIcon({ icon, bgColor, iconColor = '#fff', spin = false }) {
  return (
    <div
      className="flex items-center justify-center mx-auto"
      style={{
        width: '64px', height: '64px',
        backgroundColor: bgColor,
        borderRadius: '9999px',
        marginBottom: '20px',
      }}
    >
      <span
        className="material-symbols-outlined"
        style={{
          fontSize: '28px',
          color: iconColor,
          fontVariationSettings: "'FILL' 1",
          animation: spin ? 'spin 1s linear infinite' : 'none',
        }}
      >
        {icon}
      </span>
    </div>
  );
}


// ---- メインコンポーネント ----
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, loading: authLoading } = useAuth();

  const [invitation,  setInvitation]  = useState(null);
  const [loadError,   setLoadError]   = useState('');
  const [accepting,   setAccepting]   = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [done,        setDone]        = useState(false);

  useEffect(() => {
    if (!token) { setLoadError('招待リンクが無効です'); return; }
    getInvitation(token)
      .then(data => setInvitation(data))
      .catch(() => setLoadError('招待リンクが無効または期限切れです'));
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      await acceptInvitation({ token, transferData: false });
      setDone(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (e) {
      setAcceptError(e.message || '承認に失敗しました');
    } finally {
      setAccepting(false);
    }
  };

  // ── ローディング中 ────────────────────────────────
  if (authLoading) {
    return (
      <PageShell>
        <div className="text-center py-4">
          <StatusIcon icon="progress_activity" bgColor={C.surfaceContainerHigh} iconColor={C.primary} spin />
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '16px', color: C.onSurface }}>
            読み込み中...
          </p>
        </div>
      </PageShell>
    );
  }

  // ── エラー ────────────────────────────────────────
  if (loadError) {
    return (
      <PageShell>
        <div className="text-center py-4">
          <StatusIcon icon="cancel" bgColor={C.errorContainer} iconColor={C.onErrorContainer} />
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontSize: '20px',
              fontWeight: 700,
              color: C.onSurface,
              marginBottom: '8px',
            }}
          >
            招待リンクエラー
          </h1>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurfaceVariant, marginBottom: '24px' }}>
            {loadError}
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              fontFamily: 'Space Grotesk',
              fontSize: '14px',
              fontWeight: 600,
              color: C.primary,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: 0,
            }}
          >
            ログインページへ
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
          </button>
        </div>
      </PageShell>
    );
  }

  // ── 招待確認中 ────────────────────────────────────
  if (!invitation) {
    return (
      <PageShell>
        <div className="text-center py-4">
          <StatusIcon icon="progress_activity" bgColor={C.surfaceContainerHigh} iconColor={C.primary} spin />
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '16px', color: C.onSurface }}>
            招待を確認中...
          </p>
        </div>
      </PageShell>
    );
  }

  // ── 参加完了 ──────────────────────────────────────
  if (done) {
    return (
      <PageShell>
        <div className="text-center py-4">
          <StatusIcon icon="check_circle" bgColor={C.secondaryContainer} iconColor={C.secondary} />
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontSize: '20px',
              fontWeight: 700,
              color: C.onSurface,
              marginBottom: '8px',
            }}
          >
            参加完了！
          </h1>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurfaceVariant, marginBottom: '6px' }}>
            「{invitation.orgName}」に参加しました
          </p>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '12px', color: C.outline }}>
            ホーム画面へ移動します...
          </p>
        </div>
      </PageShell>
    );
  }

  // ── 未ログイン時 ──────────────────────────────────
  if (!isLoggedIn) {
    return (
      <PageShell>
        {/* ヘッダー */}
        <div className="text-center" style={{ marginBottom: '24px' }}>
          <StatusIcon icon="mark_email_unread" bgColor={`rgba(0,104,183,0.10)`} iconColor={C.primaryContainer} />
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontSize: '22px',
              fontWeight: 700,
              color: C.primary,
              letterSpacing: '-0.3px',
              marginBottom: '8px',
            }}
          >
            招待が届いています
          </h1>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurfaceVariant }}>
            <strong style={{ color: C.onSurface }}>{invitation.orgName}</strong> から
            {invitation.inviterName && ` ${invitation.inviterName} さんより`}
            招待されています
          </p>
        </div>

        {/* 招待詳細 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
          <InfoCell label="組織名">{invitation.orgName}</InfoCell>
          <InfoCell label="付与されるロール"><StatusBadge variant="primary">{ROLE_LABELS[invitation.role] ?? invitation.role}</StatusBadge></InfoCell>
        </div>

        {/* 注意 */}
        <div
          style={{
            display: 'flex',
            gap: '10px',
            padding: '12px 16px',
            borderRadius: '12px',
            backgroundColor: `rgba(211,228,255,0.50)`,
            border: `1px solid rgba(0,80,142,0.20)`,
            marginBottom: '24px',
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: '18px', color: C.primary, fontVariationSettings: "'FILL' 1", flexShrink: 0, marginTop: '1px' }}
          >
            info
          </span>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', color: C.primary, margin: 0 }}>
            参加するにはログインまたは新規登録が必要です
          </p>
        </div>

        {/* ボタン */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/login?redirect=/invite/${token}`)}
            icon={<span className="material-symbols-outlined" style={{ fontSize: '18px' }}>login</span>}
            className="w-full"
          >
            ログインして参加
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigate(`/register?redirect=/invite/${token}`)}
            icon={<span className="material-symbols-outlined" style={{ fontSize: '18px' }}>person_add</span>}
            className="w-full"
          >
            新規登録して参加
          </Button>
        </div>
      </PageShell>
    );
  }

  // ── ログイン済み ──────────────────────────────────
  return (
    <PageShell>
      {/* ヘッダー */}
      <div className="text-center" style={{ marginBottom: '24px' }}>
        <StatusIcon icon="mark_email_unread" bgColor={`rgba(0,104,183,0.10)`} iconColor={C.primaryContainer} />
        <h1
          style={{
            fontFamily: 'Space Grotesk',
            fontSize: '22px',
            fontWeight: 700,
            color: C.primary,
            letterSpacing: '-0.3px',
            marginBottom: '8px',
          }}
        >
          組織への招待
        </h1>
        <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurfaceVariant }}>
          <strong style={{ color: C.onSurface }}>{invitation.orgName}</strong> から招待されています
        </p>
      </div>

      {/* 招待詳細 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
        <InfoCell label="組織名">{invitation.orgName}</InfoCell>
        <InfoCell label="付与されるロール"><StatusBadge variant="primary">{ROLE_LABELS[invitation.role] ?? invitation.role}</StatusBadge></InfoCell>
        {invitation.inviterName && (
          <InfoCell label="招待者">{invitation.inviterName}</InfoCell>
        )}
      </div>

      {/* 警告 */}
      <div style={{ marginBottom: acceptError ? '12px' : '24px' }}>
        <Alert variant="warning">参加すると現在の個人データは組織に移行されます</Alert>
      </div>

      {/* エラー */}
      {acceptError && (
        <div style={{ marginBottom: '24px' }}>
          <Alert variant="error">{acceptError}</Alert>
        </div>
      )}

      {/* ボタン */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => navigate('/')}
          disabled={accepting}
          className="flex-1"
        >
          キャンセル
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={handleAccept}
          loading={accepting}
          icon={<span className="material-symbols-outlined" style={{ fontSize: '18px', fontVariationSettings: "'FILL' 1" }}>check_circle</span>}
          className="flex-1"
        >
          参加する
        </Button>
      </div>
    </PageShell>
  );
}
